import { Inject, Injectable, LOCALE_ID } from "@angular/core"
import { ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service"
import { OrganisationService } from "../organisation/organisation.service"
import { UserService } from "../user/user.service"
import { GraphData, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE } from "../common/linqset-model"
import { InformationAsset, InsightsFilter, Organisation } from "../common/organisation"
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model"
import { InsightsRefreshService } from "./insights-refresh.service"

const COMPONENT = "Analytics service"

@Injectable({
    providedIn: 'root'
})  
export class InsightsService {

    constructor(private repoService:RepositoryService, private organisationService:OrganisationService, 
        @Inject(LOCALE_ID) public locale: string, private userService:UserService, private refreshes:InsightsRefreshService) {
    }

    async loadGraphForFilter(organisation:Organisation, filter:InsightsFilter):Promise<GraphData> {
        const SERVICE = "Load graph for filter"

        const url = ORGANISATION_API(organisation._key) + "/filter"
        return await this.repoService.callSingleAPI(this.repoService.create(url, {filter}), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    async loadNodesBySuperType(organisation:Organisation, projectKey:string, superType:NODE_SUPERTYPE, filter:InsightsFilter) {
        const SERVICE = "Load nodes by super type"
        const url = ORGANISATION_API(organisation._key, projectKey) + `/insights/nodesfortype/${superType}`
        return await this.repoService.callSingleAPI(this.repoService.create(url, {filter}), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    // what is loaded once the insights of the workspace being worked out again after a change (as when the user leaves
    // a model they changed) are ready, so the page doesn't show the old ones
    async afterRefresh<T>(workspaceKey:string|undefined, load:() => Promise<T>):Promise<T> {
        await this.refreshes.settled(workspaceKey)
        return load()
    }

    async loadAnalysisGraphForFilter(organisation:Organisation, filter:InsightsFilter):Promise<{nodes:AnalysisNode[], edges:AnalysisEdge[]}> {
        const SERVICE = "Load analysis graph for filter"
        // insights being worked out again after a change would otherwise be read before they are ready
        await this.refreshes.settled(filter.workspaceKey)

        const url = ORGANISATION_API(organisation._key, filter.workspaceKey) + "/analysisdata"
        return await this.repoService.callSingleAPI(this.repoService.create(url, {filter}), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    async loadGraphForStartingNode(nodeKeys:string[], projectKey:string, modelKeys:string[]):Promise<any> {
        const SERVICE = "Load graph for node"
        const {organisation} = this.organisationService

        const modelParams = this.createParams(modelKeys, "modelKeys")
        const nodeParams = this.createParams(nodeKeys, "nodeKeys")

        const params = "?" + nodeParams + "&" + modelParams

        const url = ORGANISATION_API(organisation._key, projectKey) + "/insights/nodes" + params
        return await this.repoService.callSingleAPI(this.repoService.getData<{nodes:LinqsetNode[], edges:LinqsetEdge[]}>(url), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    async loadGraphForAsset(informationAsset:InformationAsset, projectKey:string, modelKeys:string[]):Promise<any> {
        const SERVICE = "Load graph for information asset"
        const {organisation} = this.organisationService

        const modelParams = this.createParams(modelKeys, "modelKeys")

        const params = modelParams.length>0?"?" + modelParams:""

        const url = ORGANISATION_API(organisation._key, projectKey) + "/insights/informationassets/" + ((informationAsset && informationAsset._key)?informationAsset._key:"") + params
        return await this.repoService.callSingleAPI(this.repoService.getData<{nodes:LinqsetNode[], edges:LinqsetEdge[]}>(url), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    async loadGraphForTags(tagKeys:string[], projectKey:string, modelKeys:string[]):Promise<any> {
        const SERVICE = "Load graph for tags"
        const {organisation} = this.organisationService

        const modelParams = this.createParams(modelKeys, "modelKeys")
        const tagParams = this.createParams(tagKeys, "tagKeys")

        const params = "?" + modelParams + tagParams

        const url = ORGANISATION_API(organisation._key, projectKey) + "/insights/tags/" + params 
        return await this.repoService.callSingleAPI(this.repoService.getData<{nodes:LinqsetNode[], edges:LinqsetEdge[]}>(url), COMPONENT, SERVICE, this.userService.getUser(), organisation)
    }

    private createParams(values:string[], name:string) {
        const paramList = values?values.map(v => name + "=" + v):[]
        return paramList.length > 0?paramList.join('&')+"&":''
    }
}